/**
 * OrgPassportPage — the public Social Capital Passport for one organization.
 *
 * Route: /org/:key/passport  (public, key = slug or id)
 *
 * Mirrors the individual PassportPage but built around
 * `GET /api/social-capital/org-portfolio/{key}`. Renders:
 *   • Hero — org name, verification tier, level, total SC
 *   • 8-bucket category grid with progress bars
 *   • Recent audit trail from org_point_events
 *
 * Verification tier is surfaced as a coloured shield chip; the level
 * label uses the same 5-tier ladder as individuals (New Contributor →
 * Change Maker) so the two passports feel like one system.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Building2, Loader2, ShieldCheck, MapPin, ScrollText, ArrowRight,
} from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import OrgShareBar from "@/components/OrgShareBar";

const CATEGORY_META = {
  help_people:      { label: "Help People",       color: "#10B981" },
  save_food:        { label: "Save Food",         color: "#84CC16" },
  protect_nature:   { label: "Protect Nature",    color: "#EAB308" },
  support_farmers:  { label: "Support Farmers",   color: "#F97316" },
  share_knowledge:  { label: "Share Knowledge",   color: "#EF4444" },
  reuse_give:       { label: "Reuse & Give",      color: "#A855F7" },
  volunteer_serve:  { label: "Volunteer & Serve", color: "#3B82F6" },
  build_community:  { label: "Build Community",   color: "#14B8A6" },
};

const CATEGORY_ORDER = [
  "help_people", "save_food", "protect_nature", "support_farmers",
  "share_knowledge", "reuse_give", "volunteer_serve", "build_community",
];

const TIER_STYLE = {
  platinum: { color: "#334155", bg: "#E2E8F0" },
  gold:     { color: "#92400E", bg: "#FEF3C7" },
  silver:   { color: "#475569", bg: "#F1F5F9" },
  bronze:   { color: "#B45309", bg: "#FED7AA" },
};

function fmt(n) {
  n = Number(n || 0);
  return n.toLocaleString();
}

function timeAgo(iso) {
  try {
    const then = new Date(iso).getTime();
    const s = Math.max(1, Math.floor((Date.now() - then) / 1000));
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ""; }
}

export default function OrgPassportPage() {
  const { key } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancel = false;
    setState({ loading: true, data: null, error: null });
    http
      .get(`/social-capital/org-portfolio/${encodeURIComponent(key)}`)
      .then((r) => { if (!cancel) setState({ loading: false, data: r.data, error: null }); })
      .catch((e) => {
        if (cancel) return;
        setState({ loading: false, data: null, error: e?.response?.status === 404 ? "notfound" : "error" });
      });
    return () => { cancel = true; };
  }, [key]);

  const maxCat = useMemo(() => {
    const c = state.data?.category_totals || {};
    return Math.max(1, ...Object.values(c).map((v) => Number(v || 0)));
  }, [state.data]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="org-passport-loading">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
      </div>
    );
  }
  if (state.error === "notfound") {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col items-center justify-center px-6" data-testid="org-passport-notfound">
        <Building2 className="h-10 w-10 text-stone-400 mb-3" />
        <h1 className="text-2xl font-semibold text-stone-800">Organization not found</h1>
        <p className="mt-2 text-stone-500 text-sm">No org with that handle. Check the URL and try again.</p>
        <Link to="/community/members" className="mt-4 text-emerald-700 underline text-sm">Browse contributors</Link>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600 text-sm" data-testid="org-passport-error">
        Something went wrong. Please try again.
      </div>
    );
  }

  const { org, social_capital, level, category_totals, recent_events } = state.data;
  const tier = TIER_STYLE[org.verification_tier?.toLowerCase()] || null;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="org-passport-page">
      <SeoHead
        title={`${org.name} — Social Capital Passport`}
        description={`${fmt(social_capital)} SC · ${level} · a verified record of ${org.name}'s community impact.`}
      />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: "linear-gradient(135deg,#065f46 0%,#047857 45%,#059669 100%)" }}>
        <div className="max-w-5xl mx-auto px-6 py-14 text-white">
          <div className="flex items-start gap-6">
            <div
              className="h-24 w-24 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center overflow-hidden shrink-0"
              data-testid="org-passport-avatar"
            >
              {org.image_url ? (
                <img src={org.image_url} alt={org.name} className="h-full w-full object-cover" />
              ) : (
                <Building2 className="h-12 w-12 text-white/70" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-widest text-emerald-200 font-semibold">
                <span>Organization Passport</span>
                {tier && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold"
                    style={{ color: tier.color, background: tier.bg }}
                    data-testid="org-passport-tier"
                  >
                    <ShieldCheck className="h-3 w-3" /> {org.verification_tier} verified
                  </span>
                )}
              </div>
              <h1 className="mt-2 text-4xl sm:text-5xl font-bold tracking-tight" data-testid="org-passport-name">
                {org.name}
              </h1>
              {org.mission && (
                <p className="mt-3 text-emerald-100 text-base sm:text-lg max-w-2xl">{org.mission}</p>
              )}
              <div className="mt-3 flex items-center gap-3 text-emerald-100 text-sm">
                {(org.city || org.country) && (
                  <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[org.city, org.country].filter(Boolean).join(", ")}</span>
                )}
                {org.years_active ? <span>· {org.years_active} yrs active</span> : null}
              </div>
            </div>
            <div className="hidden sm:flex flex-col items-end shrink-0">
              <div className="text-5xl font-black tabular-nums" data-testid="org-passport-total">{fmt(social_capital)}</div>
              <div className="text-emerald-200 text-xs uppercase tracking-widest font-semibold">Social Capital</div>
              <div className="mt-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold" data-testid="org-passport-level">{level}</div>
            </div>
          </div>
          <OrgShareBar
            slug={org.slug || org.id}
            name={org.name}
            points={social_capital}
          />
        </div>
      </div>

      {/* Categories */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <h2 className="text-base font-semibold text-stone-500 uppercase tracking-widest">Contribution mix</h2>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="org-passport-categories">
          {CATEGORY_ORDER.map((k) => {
            const v = Number(category_totals?.[k] || 0);
            const pct = Math.round((v / maxCat) * 100);
            const meta = CATEGORY_META[k];
            return (
              <div key={k} className="rounded-xl bg-white border border-stone-200 p-4" data-testid={`org-cat-${k}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-stone-700">{meta.label}</span>
                  <span className="tabular-nums font-semibold" style={{ color: meta.color }}>{fmt(v)}</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: meta.color }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent audit trail */}
        <h2 className="mt-10 text-base font-semibold text-stone-500 uppercase tracking-widest">Recent activity</h2>
        {(!recent_events || recent_events.length === 0) ? (
          <div className="mt-4 rounded-xl bg-white border border-dashed border-stone-300 p-8 text-center text-stone-500 text-sm" data-testid="org-passport-empty">
            <ScrollText className="h-6 w-6 mx-auto text-stone-400 mb-2" />
            No verified contributions yet. Donations to this org and volunteer signups will show up here.
          </div>
        ) : (
          <ul className="mt-4 space-y-2" data-testid="org-passport-events">
            {recent_events.map((ev) => {
              const meta = CATEGORY_META[ev.category] || null;
              return (
                <li key={ev.id} className="flex items-start gap-3 rounded-xl bg-white border border-stone-200 p-3" data-testid={`org-event-${ev.id}`}>
                  {meta && (
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full mt-1.5 shrink-0"
                      style={{ background: meta.color }}
                      title={meta.label}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-stone-800">{ev.reason}</div>
                    <div className="mt-0.5 text-xs text-stone-500">
                      {meta ? <span className="font-medium" style={{ color: meta.color }}>{meta.label}</span> : null}
                      {meta && ev.created_at ? " · " : ""}
                      {ev.created_at ? timeAgo(ev.created_at) : ""}
                    </div>
                  </div>
                  <div className="tabular-nums text-sm font-bold text-emerald-700 shrink-0">+{ev.points}</div>
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-10 flex items-center gap-4 text-sm">
          <Link
            to="/community/members"
            className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
            data-testid="org-passport-community-link"
          >
            Meet the community <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </div>
  );
}
